/**
 * atomberg-support-model-search.js
 *
 * Searchable Model autocomplete for repeatable product/item rows.
 *
 * Options come from GET {api_base_url}/api/support/serviceable-products
 * ?category_id= — an admin-curated product-master list (backend module
 * product-master), which includes legacy/discontinued models that customers
 * still raise service requests for, unlike the live Shopify catalogue.
 * category_id is normally just the selected category <option>'s own
 * `value` (the same slug already submitted to Salesforce as
 * product_category elsewhere in this form) — the backend resolves it
 * against product-master's own category names via normalized matching, so
 * this theme's category slugs never need to exactly match product-master's
 * spelling/casing. `data-category-override` (rarely set) forces a specific
 * product-master category string when normalized matching would guess
 * wrong. Rows with no category select (Register Warranty) fetch with no
 * category_id — the full list.
 *
 * The once-per-page Shopify-collection blob from
 * snippets/atomberg-support-model-data.liquid is the fallback whenever the
 * remote list is unavailable (endpoint not deployed, fetch failed, or no
 * category selected yet). Categories present in neither source silently
 * fall back to plain free-text entry — no dropdown appears, nothing blocks
 * typing.
 *
 * Exposes window.AtombergSupport.initModelSearch(scope) so
 * atomberg-support-repeatable.js can bind newly-cloned rows the same way it
 * already does for filterScopedSelects().
 */
(function () {
  'use strict';

  var w = /** @type {any} */ (window);
  if (w._atombergSupportModelSearchInit) return;
  w._atombergSupportModelSearchInit = true;

  var MAX_RESULTS = 20;

  /** @type {Record<string, Array<{title: string, sku: string}>>|null} */
  var catalogue = null;

  /** @returns {Record<string, Array<{title: string, sku: string}>>} */
  function getCatalogue() {
    if (catalogue) return catalogue;
    var script = document.querySelector('[data-support-model-data]');
    try {
      catalogue = script ? JSON.parse(script.textContent || '{}') : {};
    } catch (e) {
      catalogue = {};
    }
    return /** @type {Record<string, Array<{title: string, sku: string}>>} */ (catalogue);
  }

  /** @returns {string} */
  function getApiBaseUrl() {
    var root = document.querySelector('[data-support-api-base-url]');
    return (root && root.getAttribute('data-support-api-base-url')) || '';
  }

  /** @type {Record<string, Array<{title: string, sku: string}>>} */
  var remoteCatalogue = {};
  /** @type {Record<string, Promise<void>|undefined>} */
  var remotePending = {};

  /**
   * Kicks off (at most once per key) the serviceable-products fetch; results
   * land in remoteCatalogue. Failures leave the key unset so
   * optionsForCategory keeps using the Shopify fallback. Returns the shared
   * per-key promise so callers can re-render once the remote list lands —
   * without it, a row opened before the fetch resolves shows the Shopify
   * fallback while the next row shows product-master, and the two lists can
   * disagree visibly.
   * @param {string} key - catalogue key ('__all__' or the category value)
   * @param {string} categoryId - sent verbatim as ?category_id= ('' = full
   *   list). Normally the category select's own `value`; the backend does
   *   normalized-string matching against product-master's category names,
   *   no exact match required.
   * @returns {Promise<void>}
   */
  function ensureRemote(key, categoryId) {
    var apiBaseUrl = getApiBaseUrl();
    if (!apiBaseUrl) return Promise.resolve();
    var pending = remotePending[key];
    if (pending) return pending;
    var url =
      apiBaseUrl +
      '/api/support/serviceable-products' +
      (categoryId ? '?category_id=' + encodeURIComponent(categoryId) : '');
    pending = fetch(url, { headers: { Accept: 'application/json' } })
      .then(function (r) {
        return r.ok ? r.json() : null;
      })
      .then(function (res) {
        var data = /** @type {{products?: Array<{name?: string, sku?: string}>}|null} */ (res);
        var products = data && Array.isArray(data.products) ? data.products : [];
        if (!products.length) return;
        remoteCatalogue[key] = products.map(function (p) {
          return { title: p.name || '', sku: p.sku || '' };
        });
      })
      .catch(function () {
        // Fallback to the embedded Shopify list — nothing to do.
      });
    remotePending[key] = pending;
    return pending;
  }

  /** @param {HTMLElement} instance */
  function init(instance) {
    if (/** @type {any} */ (instance)._supportModelSearchBound) return;
    /** @type {any} */ (instance)._supportModelSearchBound = true;

    var inputEl = /** @type {HTMLInputElement|null} */ (
      instance.querySelector('[data-support-row-model]')
    );
    var listEl = /** @type {HTMLElement|null} */ (
      instance.querySelector('[data-support-model-search-list]')
    );
    if (!inputEl || !listEl) return;
    var input = inputEl;
    var list = listEl;

    var rowScope = instance.closest('[data-support-repeatable-row], [data-support-scope]');
    var categorySelect = /** @type {HTMLSelectElement|null} */ (
      rowScope ? rowScope.querySelector('[data-field="product_category"]') : null
    );

    var activeIndex = -1;

    /**
     * Starts the remote fetch for the row's current category and re-renders
     * the open suggestion list when it FIRST lands — so a dropdown opened
     * during the fetch upgrades from the Shopify fallback to product-master
     * in place instead of two rows showing different lists.
     * @param {string} key
     * @param {string} categoryId
     */
    function primeRemote(key, categoryId) {
      var hadRemote = !!remoteCatalogue[key];
      ensureRemote(key, categoryId).then(function () {
        // Only the arrival transition re-renders — an already-loaded key
        // resolving instantly must not re-enter renderList in a loop.
        if (!hadRemote && remoteCatalogue[key] && document.activeElement === input) {
          renderList();
        }
      });
    }

    /** @returns {Array<{title: string, sku: string}>} */
    function optionsForCategory() {
      // No category select in scope (e.g. Register Warranty, which has no
      // product-category step) — search across every serviceable product
      // instead of scoping to one category.
      if (!categorySelect) {
        primeRemote('__all__', '');
        return remoteCatalogue.__all__ || getCatalogue().__all__ || [];
      }
      if (!categorySelect.value) return [];
      var selectedOption = categorySelect.selectedOptions[0];
      var override = selectedOption
        ? selectedOption.getAttribute('data-category-override') || ''
        : '';
      // Default: send the category's own value — the backend normalizes and
      // matches it against product-master, no metaobject field required.
      primeRemote(categorySelect.value, override || categorySelect.value);
      return remoteCatalogue[categorySelect.value] || getCatalogue()[categorySelect.value] || [];
    }

    function closeList() {
      list.hidden = true;
      list.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
      input.removeAttribute('aria-activedescendant');
      activeIndex = -1;
    }

    /** @param {number} index */
    function setActive(index) {
      var items = list.querySelectorAll('[role="option"]');
      items.forEach(function (item, i) {
        item.classList.toggle('atomberg-support-model-search__option--active', i === index);
      });
      var activeItem = /** @type {HTMLElement|undefined} */ (items[index]);
      if (activeItem) {
        input.setAttribute('aria-activedescendant', activeItem.id);
        activeItem.scrollIntoView({ block: 'nearest' });
      } else {
        input.removeAttribute('aria-activedescendant');
      }
      activeIndex = index;
    }

    /** @param {{title: string, sku: string}} product */
    function selectProduct(product) {
      input.value = product.title;
      // The reference submits model + sku per item; picking a model fills
      // the row's hidden sku just like the serial lookup does.
      var skuInput = /** @type {HTMLInputElement|null} */ (
        rowScope ? rowScope.querySelector('[data-support-row-sku]') : null
      );
      if (skuInput && product.sku) skuInput.value = product.sku;
      closeList();
    }

    function renderList() {
      var query = input.value.trim().toLowerCase();
      var matches = optionsForCategory()
        .filter(function (p) {
          return !query || p.title.toLowerCase().indexOf(query) !== -1;
        })
        .slice(0, MAX_RESULTS);

      if (!matches.length) {
        closeList();
        return;
      }

      list.innerHTML = '';
      matches.forEach(function (product, i) {
        var item = document.createElement('li');
        item.id = 'atomberg-support-model-option-' + Math.random().toString(36).slice(2);
        item.setAttribute('role', 'option');
        item.className = 'atomberg-support-model-search__option';
        item.textContent = product.title;
        // Use mousedown, not click — it fires before the input's blur handler
        // closes the list, so the selection registers instead of vanishing.
        item.addEventListener('mousedown', function (e) {
          e.preventDefault();
          selectProduct(product);
        });
        list.appendChild(item);
      });
      list.hidden = false;
      input.setAttribute('aria-expanded', 'true');
      setActive(-1);
    }

    input.addEventListener('input', renderList);
    input.addEventListener('focus', function () {
      if (optionsForCategory().length) renderList();
    });

    input.addEventListener('blur', function () {
      // Deferred so a pending option mousedown's preventDefault/selection can
      // land before the list gets torn down.
      setTimeout(closeList, 0);
    });

    input.addEventListener('keydown', function (e) {
      if (list.hidden) return;
      var items = list.querySelectorAll('[role="option"]');
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActive(Math.min(activeIndex + 1, items.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActive(Math.max(activeIndex - 1, 0));
      } else if (e.key === 'Enter') {
        if (activeIndex === -1) return;
        e.preventDefault();
        var query = input.value.trim().toLowerCase();
        var matches = optionsForCategory().filter(function (p) {
          return !query || p.title.toLowerCase().indexOf(query) !== -1;
        });
        var product = matches[activeIndex];
        if (product) selectProduct(product);
      } else if (e.key === 'Escape') {
        closeList();
      }
    });

    if (categorySelect) {
      categorySelect.addEventListener('change', function () {
        // A model belongs to the category it was picked under — switching
        // category (directly, or via a product-type change wiping the
        // select) would silently submit a model from the wrong category.
        input.value = '';
        var skuInput = /** @type {HTMLInputElement|null} */ (
          rowScope ? rowScope.querySelector('[data-support-row-sku]') : null
        );
        if (skuInput) skuInput.value = '';
        closeList();
        // Warm the product-master fetch NOW, while the user is still on the
        // fields between category and model — by focus time the remote list
        // has usually landed, so rows never disagree on their suggestions.
        optionsForCategory();
      });
    }
  }

  /** @param {HTMLElement} [scope] */
  function initModelSearch(scope) {
    var root = scope || document;
    root.querySelectorAll('[data-support-model-search]').forEach(function (el) {
      init(/** @type {HTMLElement} */ (el));
    });
  }

  w.AtombergSupport = w.AtombergSupport || {};
  w.AtombergSupport.initModelSearch = initModelSearch;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initModelSearch();
    });
  } else {
    initModelSearch();
  }
})();
